import axios from "axios";
import { alertFailed } from "@/functions/swAlert";
import { msalInstance } from "@/authConfig";
import { createFetch } from "@vueuse/core";
import {
  getEffectiveAuthToken,
  getIsHandlingSessionExpired,
  setIsHandlingHubAccessRequired,
  setIsHandlingSessionExpired,
} from "@/utils/authToken";

let baseUrl = process.env.VUE_APP_IS_BRANCH_PROD == "1" ? process.env.VUE_APP_ENDPOINT_API_PROD : process.env.VUE_APP_ENDPOINT_API_TEST;
const FCH_UNAUTHORIZED_MESSAGE =
  "L’accesso al sistema può avvenire solo tramite <strong>FITP CONNECT HUB</strong>.<br>Chiudendo il pop-up sarai automaticamente reindirizzato alla pagina di login.<br>Ti ricordiamo che sarà necessario utilizzare le medesime credenziali del Management";
const FCH_UNAUTHORIZED_MESSAGES = ["Non autorizzato.", "L'accesso per gli utenti dei circoli è consentito solo tramite Connect HUB."];
const FCH_UNAUTHORIZED_ERROR_CODE = "HUB_ACCESS_REQUIRED";
const CONNECT_HUB_URL = "https://connecthub.fitp.it/";
const SESSION_EXPIRED_MESSAGE = "Sessione scaduta. Per continuare, effettuare nuovamente il login";
let isHandlingFchUnauthorized = false;

if (baseUrl && !baseUrl.endsWith("/api")) {
  baseUrl = baseUrl + "/api";
}

export const baseUrlApiApp = baseUrl;

const goToFCH = () => {
  window.location.assign(CONNECT_HUB_URL);
};

const isFchUnauthorizedResponse = (status?: number, data?: unknown) => {
  const errorCode = (data as { errorCode?: string } | undefined)?.errorCode;
  // const messages = Array.isArray((data as { messages?: unknown[] } | undefined)?.messages)
  //   ? (data as { messages: unknown[] }).messages ?? []
  //   : [];

  return errorCode === FCH_UNAUTHORIZED_ERROR_CODE;

  // if (errorCode === FCH_UNAUTHORIZED_ERROR_CODE) return true;

  // return status === 401 && messages.some(message => FCH_UNAUTHORIZED_MESSAGES.includes(String(message)));
};

const handleFchUnauthorized = async (status?: number, data?: unknown) => {
  if (!isFchUnauthorizedResponse(status, data) || isHandlingFchUnauthorized) return false;

  isHandlingFchUnauthorized = true;
  setIsHandlingHubAccessRequired(true);
  try {
    await alertFailed(FCH_UNAUTHORIZED_MESSAGE);
    goToFCH();
  } finally {
    isHandlingFchUnauthorized = false;
    setIsHandlingHubAccessRequired(false);
  }

  return true;
};

const logoutAndRedirectToLogin = () => {
  // Rimuoviamo i token API custom: MSAL pulisce solo le proprie chiavi, non queste.
  localStorage.removeItem("tokenAdb2c");
  localStorage.removeItem("FCHT");
  msalInstance.logoutRedirect();
};

// Gestisce le risposte 401 (sessione scaduta / token non valido): mostra un solo popup anche
// se più chiamate falliscono in parallelo e, alla chiusura, riporta l'utente al login.
// Va chiamata dopo handleFchUnauthorized, che ha la precedenza per i 401 "HUB_ACCESS_REQUIRED".
const handleSessionExpired = (status?: number) => {
  if (status !== 401) return false;
  if (getIsHandlingSessionExpired()) return true;

  setIsHandlingSessionExpired(true);
  alertFailed(SESSION_EXPIRED_MESSAGE).then(logoutAndRedirectToLogin);

  return true;
};

export const dbManagApp = axios.create({
  baseURL: baseUrlApiApp,
});

dbManagApp.interceptors.request.use(config => {
  const token = getEffectiveAuthToken();
  if (token) config.headers["Authorization"] = "Bearer " + token;
  return config;
});

export const requestAppPost = async (endpoint: string, payload: Record<string, unknown>, noAlert) => {
  return dbManagApp
    .post(endpoint, payload)
    .then(res => {
      return {
        ...res,
      };
    })
    .catch(async e => {
      console.log(e);
      if (await handleFchUnauthorized(e?.response?.status, e?.response?.data)) {
        return {
          ...e.response,
        };
      }
      if (handleSessionExpired(e?.response?.status)) {
        return {
          ...e.response,
        };
      }
      if (!noAlert) {
        alertFailed(e.response.data.message ? e.response.data.message : "Attenzione! Si è verificato un errore. Riprovare più tardi");
      }

      if (e.response.data.message == "Utente non autorizzato") {
        msalInstance.logoutRedirect();
      }

      return {
        ...e.response,
      };
    });
};

export const requestAppPostNoAlert = async (endpoint: string, payload: Record<string, unknown>) => {
  console.log("this post");
  return dbManagApp
    .post(endpoint, payload)
    .then(res => {
      return {
        ...res,
      };
    })
    .catch(async e => {
      if (await handleFchUnauthorized(e?.response?.status, e?.response?.data)) {
        return {
          ...e.response,
        };
      }
      if (handleSessionExpired(e?.response?.status)) {
        return {
          ...e.response,
        };
      }
      return {
        ...e.response,
      };
    });
};

let controller;

export const abortRequest = () => {
  controller.abort("Ricerca utente");
};

export const requestAppGet = async (endpoint: string, params: Record<string, unknown>) => {
  console.log("requestAppGet", endpoint, params);
  return dbManagApp
    .get(endpoint, params)
    .then(res => {
      return {
        ...res,
      };
    })
    .catch(async e => {
      if (await handleFchUnauthorized(e?.response?.status, e?.response?.data)) {
        return {
          ...e.response,
        };
      }
      if (handleSessionExpired(e?.response?.status)) {
        return {
          ...e.response,
        };
      }
      alertFailed(e.response.data.message ? e.response.data.message : "Attenzione! Si è verificato un errore. Riprovare più tardi");

      return {
        ...e.response,
      };
    });
};

export const requestAppGetNoAlert = async (endpoint: string, params: Record<string, unknown>) => {
  return dbManagApp
    .get(endpoint, params)
    .then(res => {
      return {
        ...res,
      };
    })
    .catch(async e => {
      if (await handleFchUnauthorized(e?.response?.status, e?.response?.data)) {
        return {
          ...e.response,
        };
      }
      if (handleSessionExpired(e?.response?.status)) {
        return {
          ...e.response,
        };
      }
      return {
        ...e.response,
      };
    });
};

export const sendFile = axios.create({
  baseURL: `${baseUrlApiApp}`,
  // data: bodyFormData,
  headers: { "Content-Type": "multipart/referto-data" },
  onUploadProgress: function (progressEvent) {
    console.log("cambio percentuale upload file", progressEvent);
    // let percentCompleted = Math.round(
    //   (progressEvent.loaded * 100) / progressEvent.total
    // );
  },

  /* other custom settings */
});
sendFile.interceptors.request.use(config => {
  const token = getEffectiveAuthToken();
  if (token) {
    config.headers["Authorization"] = "bearer " + token;
  }
  return config;
});
export const fileSender = async (apiLink, bodyFormData) => {
  return sendFile
    .post(apiLink, bodyFormData)
    .then(res => {
      return {
        ...res,
      };
    })
    .catch(async e => {
      if (await handleFchUnauthorized(e?.response?.status, e?.response?.data)) {
        return {
          ...e.response,
        };
      }
      if (handleSessionExpired(e?.response?.status)) {
        return {
          ...e.response,
        };
      }
      alertFailed(
        e && e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : "Attenzione! Si è verificato un errore. Riprovare più tardi"
      );
      return {
        ...e.response,
      };
    });
};

const dbManagControlloTokenFCH = axios.create({
  baseURL: baseUrlApiApp,
  // headers: {
  //   Authorization: "Bearer " + localStorage.getItem("id_token"),
  // },
  /* other custom settings */
});

export const useFetchJSON = createFetch({
  baseUrl: baseUrlApiApp,
  combination: "overwrite",
  options: {
    immediate: false,
    // beforeFetch in pre-configured instance will only run when the newly spawned instance do not pass beforeFetch
    async beforeFetch({ options }) {
      const token = getEffectiveAuthToken();
      options.headers = {
        ...options.headers,
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      return { options };
    },
    async onFetchError(ctx) {
      // Parse error response
      if (ctx.response) {
        if (ctx.error && ctx.error.name === "AbortError") {
          ctx.error = null;
          return ctx;
        }

        try {
          const errorData = await ctx.response.clone().json();
          ctx.data = errorData;
          ctx.error = errorData;
          if (!(await handleFchUnauthorized(ctx.response.status, errorData))) handleSessionExpired(ctx.response.status);
        } catch (e) {
          // If response is not JSON
          const errorText = await ctx.response.clone().text();
          ctx.error = errorText;
          handleSessionExpired(ctx.response.status);
        }
      }
      return ctx;
    },
  },
});

export const requests = {
  useFetchJSON,
  dbManag: dbManagApp,
  baseUrlApiApp,
  dbManagControlloTokenFCH,
};
